"use client";

import Link from "next/link";
import { CategoryTree } from "@/components/files/CategoryTree";
import { FileList } from "@/components/files/FileList";
import type {
  FileCategoryLayoutProps,
  FilesDirectoryLayoutProps,
} from "./types";

function CategoryCards({
  tree,
  locale,
  isAr,
}: Pick<FilesDirectoryLayoutProps, "tree" | "locale" | "isAr">) {
  if (tree.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center text-muted-foreground">
        {isAr ? "لا توجد تصنيفات بعد." : "No categories yet."}
      </div>
    );
  }

  return (
    <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {tree.map((node) => {
        const childCount = node.children?.length ?? 0;
        return (
          <li key={node.id}>
            <Link
              href={`/${locale}/files/${node.slug}`}
              className="group flex h-full flex-col rounded-2xl border border-border/80 bg-card p-5 shadow-sm transition hover:border-[color-mix(in_srgb,var(--association-theme-color-1)_45%,transparent)] hover:shadow-md"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[color-mix(in_srgb,var(--association-theme-color-1)_12%,transparent)] text-[var(--association-theme-color-1)]">
                <svg
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 7.5A1.5 1.5 0 0 1 5.25 6h4.19c.4 0 .78.16 1.06.44l.81.81c.28.28.66.44 1.06.44h6.38A1.5 1.5 0 0 1 20.25 9v7.5a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-9Z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold tracking-tight group-hover:text-[var(--association-theme-color-1)]">
                {node.title}
              </h3>
              {node.description ? (
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                  {node.description}
                </p>
              ) : null}
              {childCount > 0 ? (
                <p className="mt-auto pt-4 text-xs text-muted-foreground">
                  {isAr
                    ? `${childCount} تصنيف فرعي`
                    : `${childCount} subcategor${childCount === 1 ? "y" : "ies"}`}
                </p>
              ) : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export function SidebarDirectoryLayout({
  tree,
  locale,
  isAr,
}: FilesDirectoryLayoutProps) {
  return (
    <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-10">
      <aside className="min-w-0">
        <div className="lg:sticky lg:top-24">
          <CategoryTree
            nodes={tree}
            locale={locale}
            title={isAr ? "التصنيفات" : "Categories"}
          />
        </div>
      </aside>
      <section className="min-w-0">
        <p className="mb-6 max-w-3xl text-base leading-relaxed text-muted-foreground">
          {isAr
            ? "تصفح التصنيفات للاطلاع على الملفات والسياسات والتقارير."
            : "Browse categories to view policies, reports, and documents."}
        </p>
        <CategoryCards tree={tree} locale={locale} isAr={isAr} />
      </section>
    </div>
  );
}

export function SidebarCategoryLayout({
  tree,
  locale,
  isAr,
  activeSlug,
  category,
  files,
}: FileCategoryLayoutProps) {
  return (
    <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-10">
      <aside className="min-w-0">
        <div className="lg:sticky lg:top-24">
          <CategoryTree
            nodes={tree}
            locale={locale}
            activeSlug={activeSlug}
            title={isAr ? "التصنيفات" : "Categories"}
          />
        </div>
      </aside>
      <section className="min-w-0">
        {category.description ? (
          <p className="mb-6 max-w-3xl text-base leading-relaxed text-muted-foreground">
            {category.description}
          </p>
        ) : null}
        <FileList files={files} isAr={isAr} />
      </section>
    </div>
  );
}
